import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ComparatorService } from '../../shared/services/comparator-service';
import { KompaktEigentumanteilPipe } from '../../shared/kompaktdarstellung/eigentumanteil/kompakt-eigentumanteil.pipe';
import {
  BerechtigtesGrundstueckMeta,
  CapiCodeTextEntryMeta,
  CodeEntry,
  EigentuemerEntryMeta,
  EigentumanteilMasterDetailEntryMeta,
  EigentumanteilMasterIdMeta,
  EigentumanteilVersionDetailEntryMeta,
  FieldMetaData,
  GbeigentumService,
  GbpersonService,
  GrundstueckId,
  LoadEigentumanteilSichtByGrundstueckIdResponseMeta,
  PersonGbMasterDetailEntryMeta,
  PersonGbVersionDetailEntryMeta,
  RechtsgrundEntryMeta
} from '../gb-api';
import { HistoryMode } from './HistoryMode';
import { PersonStammMasterDetailEntryMeta, PersonStammMasterId, PersonstammpersonService } from '../personstamm-api';
import { translateName } from '../../shared/functions/language.functions';
import { TimepointCtx, TimepointService } from './timepoint.service';
import { AppConfigService } from './app-config.service';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { InfinitLookupResult } from '../../shared/components/list-loader/infinit-lookup-result';
import { HttpErrorResponse } from '@angular/common/http';
import { AppPermissionService } from './app-permission.service';

@Injectable()
export class EigentumViewmodelService {

  constructor(public translateService: TranslateService,
              private eigentumService: GbeigentumService,
              private personService: GbpersonService,
              private timepointService: TimepointService,
              private appConfigService: AppConfigService,
              private eigentumanteilPipe: KompaktEigentumanteilPipe,
              private comperatorService: ComparatorService,
              private personstammService: PersonstammpersonService,
              private permissionService: AppPermissionService) {
  }

  loadSichtByGrundstueckId(id: GrundstueckId,
                           eigentumErweitert: boolean,
                           mitAddressen: boolean,
                           checkEinstellungHistorischeDaten: boolean,
                           offset: number,
                           limit: number,
                           subscriptions: Array<Subscription>,
                           isEigentuemer?: boolean): Observable<EigentumanteilSichtGrundstueckVm> {
    const histData = checkEinstellungHistorischeDaten ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;

    return this.eigentumService.loadEigentumSichtGrundstueck(
      id.id,
      eigentumErweitert,
      'BELASTET',
      histData,
      this.timepointService.timepointCtx.timepointIso,
      this.timepointService.timepointCtx.timepointId,
      offset,
      limit
    )
      .pipe(catchError(err => {
        const ctx = this.getViewModelCtx(this.timepointService.timepointCtx, mitAddressen, subscriptions);
        return of(<LoadEigentumanteilSichtByGrundstueckIdResponseMeta>{
          entries: [new EigentumanteilMasterDetailVM(null, err, 0, ctx, this.permissionService)],
          lastJunk: true
        });
      }))
      .pipe(map(res => {
        const sortedEigentumAnteile = res.entries.sort((item1, item2) => {
          return this.comperatorService.eigentumanteilMasterComparator(item1, item2);
        });
        const ctx = this.getViewModelCtx(this.timepointService.timepointCtx, mitAddressen, subscriptions, isEigentuemer);
        const infinitiveLookupResult = {
          result: sortedEigentumAnteile.map(e => new EigentumanteilMasterDetailVM(e, null, 0, ctx, this.permissionService)),
          lastJunk: res.lastJunk
        };
        return new EigentumanteilSichtGrundstueckVm(infinitiveLookupResult, res);
      }));
  }

  loadSichtByGrundstueckIdGbMeldung(id: GrundstueckId,
                                    eigentumErweitert: boolean,
                                    mitAddressen: boolean,
                                    checkEinstellungHistorischeDaten: boolean,
                                    offset: number,
                                    limit: number,
                                    subscriptions: Array<Subscription>): Observable<EigentumanteilSichtGrundstueckVm> {
    const histData = checkEinstellungHistorischeDaten ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;

    return this.eigentumService.loadEigentumSichtGbMeldung(
      id.id,
      eigentumErweitert,
      'BELASTET',
      histData,
      this.timepointService.timepointCtx.timepointIso,
      this.timepointService.timepointCtx.timepointId,
      offset,
      limit
    )
      .pipe(catchError(err => {
        const ctx = this.getViewModelCtx(this.timepointService.timepointCtx, mitAddressen, subscriptions);
        return of(<LoadEigentumanteilSichtByGrundstueckIdResponseMeta>{
          entries: [new EigentumanteilMasterDetailVM(null, err, 0, ctx, this.permissionService)],
          lastJunk: true
        });
      }))
      .pipe(map(res => {
        const sortedEigentumAnteile = res.entries.sort((item1, item2) => {
          return this.comperatorService.eigentumanteilMasterComparator(item1, item2);
        });
        const ctx = this.getViewModelCtx(this.timepointService.timepointCtx, mitAddressen, subscriptions);
        const infinitiveLookupResult = {
          result: sortedEigentumAnteile.map(e => new EigentumanteilMasterDetailVM(e, null, 0, ctx, this.permissionService)),
          lastJunk: res.lastJunk
        };
        return new EigentumanteilSichtGrundstueckVm(infinitiveLookupResult, res);
      }));
  }

  private getViewModelCtx(z: TimepointCtx, mitAddressen: boolean, subscriptions: Array<Subscription>, isEigentuemer?: boolean): ViewModelCtx {
    return <ViewModelCtx>{
      timepointId: z.timepointId,
      timepoint: z.timepointIso,
      einstellungHistorischeDaten: this.appConfigService.einstellungHistorischeDaten,
      personService: this.personService,
      comparatorService: this.comperatorService,
      translateService: this.translateService,
      eigentumanteilPipe: this.eigentumanteilPipe,
      personstammService: this.personstammService,
      mitAdressen: mitAddressen,
      isEigentuemer: isEigentuemer ?? false,
      subscriptions: subscriptions
    };
  }

}

export interface ViewModelCtx {
  translateService: TranslateService;
  comparatorService: ComparatorService;
  eigentumanteilPipe: KompaktEigentumanteilPipe;
  personService: GbpersonService;
  einstellungHistorischeDaten: HistoryMode.HistoryMode;
  timepoint: string;
  timepointId: string;
  personstammService: PersonstammpersonService;
  mitAdressen: boolean;
  isEigentuemer: boolean;
  subscriptions: Array<Subscription>;
}

export class EigentumanteilSichtGrundstueckVm {

  private readonly response: LoadEigentumanteilSichtByGrundstueckIdResponseMeta;

  constructor(public entries: InfinitLookupResult<EigentumanteilMasterDetailVM>, response: LoadEigentumanteilSichtByGrundstueckIdResponseMeta) {
    this.response = response;
    this.entries = entries;
  }

  get ersterfassungUebergeben() {
    return this.response.ersterfassungUebergeben;
  }

}

export class EigentumanteilMasterDetailVM {

  berechtigtesGrundstueckVm: BerechtigtesGrundstueckVm;
  berechtigtePersonenVm: EigentuemerVm[];
  id: EigentumanteilMasterIdMeta;
  versionenVm: EigentumanteilVersionVm[];
  externeHinweiseGruppen: ExterneHinweiseGruppeVm[];
  showBelegColumn = true;

  constructor(public object: EigentumanteilMasterDetailEntryMeta,
              public httpResponseError: HttpErrorResponse,
              public level: number,
              private ctx: ViewModelCtx,
              private permissionService: AppPermissionService) {
    if (object) {
      this.berechtigtesGrundstueckVm = object.berechtigtesGrundstueck ? new BerechtigtesGrundstueckVm(object.berechtigtesGrundstueck, this.level, ctx, this.permissionService) : undefined;
      this.berechtigtePersonenVm = object.berechtigtePersonen ? object.berechtigtePersonen
        .filter(b => b.isRoot)
        .map(b => new EigentuemerVm(b, object.berechtigtePersonen, this.level, ctx, this.permissionService)) : new Array<EigentuemerVm>();
      this.berechtigtePersonenVm = this.berechtigtePersonenVm.sort((a, b) => ctx.comparatorService.personGbMasterComparator(a.personGbMasterVm.object, b.personGbMasterVm.object));
      this.versionenVm = new Array<EigentumanteilVersionVm>();
      if (object.versions) {
        for (let i = 0; i < object.versions.length; i++) {
          this.versionenVm.push(new EigentumanteilVersionVm(object.versions[i], i === object.versions.length - 1, ctx));
        }
        this.versionenVm = this.versionenVm.sort((a, b) => ctx.comparatorService.eigentumanteilVersionComparator(a.object, b.object));
      }
      this.externeHinweiseGruppen = this.calculateExterneGruppen();

      this.showBelegColumn = object.mRechtsgruende?.access !== FieldMetaData.AccessEnum.DEACTIVATED;
    }
  }

  get hasRightRechtsgruende() {
    return this.object.mRechtsgruende.access !== FieldMetaData.AccessEnum.NONE;
  }

  get hasExterneHinweise(): boolean {
    return this.object.externeHinweise && this.object.externeHinweise.length > 0;
  }

  get grundstueckEigentum(): boolean {
    return this.object.eigentumanteilArt === EigentumanteilMasterDetailEntryMeta.EigentumanteilArtEnum.GRUNDSTUECK;
  }

  get indent() {
    return {
      'padding-left': this.level * 10 + 'px'
    };
  }

  get isRoot() {
    return this.level === 0;
  }

  private calculateExterneGruppen(): ExterneHinweiseGruppeVm[] {
    const res = new Array<ExterneHinweiseGruppeVm>();
    const capiCodesIds = this.object.externeHinweise ? new Set<string>(this.object.externeHinweise.map(e => e.codeEntry.id.id)) : new Set<string>();

    capiCodesIds.forEach(capiCodeId => {
      const codeEntries = this.object.externeHinweise.filter(e => e.codeEntry.id.id === capiCodeId);
      const codeTextEntriesVm = this.object.externeHinweise.filter(e => e.codeEntry.id.id === capiCodeId).map(e => new CodeTextEntryVm(e));
      res.push(new ExterneHinweiseGruppeVm(codeEntries[0].codeEntry, codeTextEntriesVm, this.ctx));
    });
    return res.sort((a, b) => this.ctx.comparatorService.translatedStringComparator(a.name, b.name));
  }

}

export class EigentumanteilVersionVm {

  constructor(public object: EigentumanteilVersionDetailEntryMeta,
              public showContent: boolean,
              public ctx: ViewModelCtx) {
  }

  get art(): string {
    return this.ctx.eigentumanteilPipe.transform(this.object, 'EIGENTUMANTEIL_TYP01');
  }

  get quoteValue(): string {
    let res = '';
    if (this.displayQuote()) {
      res = this.object.quote.zaehler + '/' + this.object.quote.nenner;
    } else if (this.object.quoteUnbekannt) {
      res = this.ctx.translateService.instant('UNBEKANNT');
    } else if (this.object.quoteAlsText) {
      res = this.object.quoteAlsText;
    }
    return res;
  }

  private displayQuote(): boolean {
    if (!this.object.quote) {
      return false;
    }
    if (this.object.quote.zaehler === 1 && this.object.quote.nenner === 1) {
      return false;
    }
    return !(this.object.quote.zaehler === 0 && this.object.quote.nenner === 0);
  }
}

export class BerechtigtesGrundstueckVm {

  belastendeEigentumsanteileVm: EigentumanteilMasterDetailVM[];

  constructor(public object: BerechtigtesGrundstueckMeta, level: number, ctx: ViewModelCtx, private permissionService: AppPermissionService) {
    this.belastendeEigentumsanteileVm = object.belastendeEigentumsanteile.map(b => new EigentumanteilMasterDetailVM(b, null, level + 1, ctx, this.permissionService));
  }
}

export class EigentuemerVm {

  personGbMasterVm: PersonGbMasterVm;

  constructor(public object: EigentuemerEntryMeta, berechtigt: EigentuemerEntryMeta[], level: number, ctx: ViewModelCtx, private permissionService: AppPermissionService) {
    if (!object.isRoot) {
      return;
    }
    const masterId = object.personGbMasterId !== null ? object.personGbMasterId.id : null;
    const versionId = object.personGbVersionId !== null ? object.personGbVersionId.id : null;
    const sub = ctx.personService.loadPersonGbById(
      ctx.einstellungHistorischeDaten,
      masterId,
      versionId,
      ctx.timepoint,
      ctx.timepointId)
      .subscribe(p => {
        this.personGbMasterVm = new PersonGbMasterVm(p, berechtigt, level + 1, ctx, this.permissionService);
      });
    ctx.subscriptions.push(sub);
  }
}

export class PersonGbMasterVm {

  rechtsgruende: Array<RechtsgrundEntryMeta>;
  rechtsgruendeDatePublic: Array<string>;
  mitgliederVm: PersonGbMasterVm[];
  versionenVm: PersonGbVersionVm[];
  level: number;
  personStamm: PersonStammMasterDetailEntryMeta;
  isAuskunftGesperrt: boolean;
  isAddresseGesperrt: boolean;

  constructor(public object: PersonGbMasterDetailEntryMeta,
              berechtigt: EigentuemerEntryMeta[],
              level: number,
              ctx: ViewModelCtx,
              private permissionService: AppPermissionService) {
    this.isAuskunftGesperrt = object.hasAuskunftssperre === PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum.AUSKUNFT_SPERRE;
    this.isAddresseGesperrt = object.hasAuskunftssperre === PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum.ADRESS_SPERRE;
    const eigentuemerEntries: EigentuemerEntryMeta[] = berechtigt.filter(b => this.filterMatchingEigentuemer(b));
    if (eigentuemerEntries.length === 0) {
      this.rechtsgruende = new Array<RechtsgrundEntryMeta>();
      this.rechtsgruendeDatePublic = new Array<string>();
    } else {
      this.rechtsgruende = eigentuemerEntries[0].rechtsgrundEntries;
      this.rechtsgruendeDatePublic = eigentuemerEntries[0].rechtsgrundDatePublic;
    }
    if (ctx.mitAdressen && ((!this.isAddresseGesperrt && !this.isAuskunftGesperrt) || ctx.isEigentuemer)) {
      this.loadPersonstamm(object.personstammMasterId, ctx);
    }
    this.level = level;
    this.mitgliederVm = object.mitglieder.map(m => new PersonGbMasterVm(m, berechtigt, level + 1, ctx, this.permissionService));
    this.versionenVm = new Array<PersonGbVersionVm>();
    for (let i = 0; i < object.versions.length; i++) {
      this.versionenVm.push(new PersonGbVersionVm(object.versions[i], i === object.versions.length - 1, ctx));
    }
    this.versionenVm = this.versionenVm.sort((a, b) => ctx.comparatorService.erstellzeitpunktComparator(a.object, b.object));
  }

  get indent() {
    return {
      'padding-left': this.level * 10 + 'px'
    };
  }

  private filterMatchingEigentuemer(b) {
    if (b.personGbVersionId) {
      const res = this.object.versions.map(v => {
        return v.id.id;
      }).includes(b.personGbVersionId.id);
      return res;
    }
    if (b.personGbMasterId) {
      return b.personGbMasterId.id === this.object.id.id;
    }
    return true;
  }

  private loadPersonstamm(personStammMaterId: PersonStammMasterId, ctx: ViewModelCtx) {
    if (personStammMaterId) {
      ctx.personstammService.loadById(personStammMaterId.id, HistoryMode.HistoryMode.OHNE_HIST, null, ctx.timepoint, null)
        .subscribe(entry => {
          this.personStamm = entry;
        });
    }
  }
}

export class PersonGbVersionVm {

  constructor(public object: PersonGbVersionDetailEntryMeta,
              public showContent: boolean,
              ctx: ViewModelCtx) {

  }
}

export class ExterneHinweiseGruppeVm {

  constructor(public code: CodeEntry,
              private _values: CodeTextEntryVm[],
              private ctx: ViewModelCtx) {
  }

  get name() {
    if (this.code) {
      return translateName(this.code.text, this.ctx.translateService);
    } else {
      return '';
    }
  }

  get values(): CodeTextEntryVm[] {
    return this._values.sort((a, b) => this.ctx.comparatorService.orderByComparator(a.object.text, b.object.text));
  }
}

export class CodeTextEntryVm {

  constructor(public object: CapiCodeTextEntryMeta) {

  }
}
